import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import seoConfig from '../../seo.config.json'

type SeoPage = (typeof seoConfig.pages)[number]

const SITE_URL = seoConfig.siteUrl.replace(/\/+$/, '')

export default function SeoManager() {
  const location = useLocation()

  useEffect(() => {
    const pathname = normalizePathname(location.pathname)
    const page = resolvePage(pathname)
    const canonicalUrl = `${SITE_URL}${page.path === '/' ? '/' : page.path}`
    const robots = 'robots' in page && page.robots ? page.robots : seoConfig.defaultRobots

    document.title = page.title
    setMeta('name', 'description', page.description)
    setMeta('name', 'robots', robots)
    setMeta('property', 'og:type', 'website')
    setMeta('property', 'og:locale', 'zh_CN')
    setMeta('property', 'og:site_name', seoConfig.siteName)
    setMeta('property', 'og:title', page.title)
    setMeta('property', 'og:description', page.description)
    setMeta('property', 'og:url', canonicalUrl)
    setMeta('name', 'twitter:card', 'summary')
    setMeta('name', 'twitter:title', page.title)
    setMeta('name', 'twitter:description', page.description)
    setCanonical(canonicalUrl)
    setJsonLd(buildJsonLd(page, canonicalUrl))
  }, [location.pathname])

  return null
}

function resolvePage(pathname: string): SeoPage {
  const staticPage = seoConfig.pages.find(page => page.path === pathname)
  if (staticPage) return staticPage

  if (/^\/community\/[^/]+$/.test(pathname)) {
    return {
      path: pathname,
      output: 'community-detail.html',
      title: '商业航天社区内容｜Space Since',
      description: '查看 Space Since 商业航天社区用户发布的讨论与来源。',
      schemaType: 'WebPage',
      robots: 'noindex,follow',
      includeInSitemap: false,
    }
  }

  if (pathname === '/search') {
    return {
      path: '/search',
      output: 'search.html',
      title: '搜索｜Space Since',
      description: '搜索 Space Since 商业航天资讯、事件和公司动态。',
      schemaType: 'SearchResultsPage',
      robots: 'noindex,follow',
      includeInSitemap: false,
    }
  }

  return seoConfig.pages[0]
}

function normalizePathname(pathname: string) {
  if (pathname === '/') return '/'
  return pathname.replace(/\/+$/, '')
}

function buildJsonLd(page: SeoPage, canonicalUrl: string) {
  const pageData = {
    '@type': page.schemaType,
    '@id': `${canonicalUrl}#webpage`,
    name: page.title,
    description: page.description,
    url: canonicalUrl,
    inLanguage: 'zh-CN',
    isPartOf: { '@id': `${SITE_URL}/#website` },
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: seoConfig.siteName,
        url: `${SITE_URL}/`,
        inLanguage: 'zh-CN',
        publisher: { '@id': `${SITE_URL}/#organization` },
        hasPart: seoConfig.pages
          .filter(item => item.includeInSitemap)
          .map(item => ({
            '@type': 'WebPage',
            name: item.title,
            url: `${SITE_URL}${item.path === '/' ? '/' : item.path}`,
          })),
      },
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: seoConfig.siteName,
        url: `${SITE_URL}/`,
      },
      pageData,
    ],
  }
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.content = content
}

function setCanonical(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!element) {
    element = document.createElement('link')
    element.rel = 'canonical'
    document.head.appendChild(element)
  }
  element.href = url
}

function setJsonLd(data: Record<string, unknown>) {
  let element = document.getElementById('seo-jsonld') as HTMLScriptElement | null
  if (!element) {
    element = document.createElement('script')
    element.type = 'application/ld+json'
    element.id = 'seo-jsonld'
    document.head.appendChild(element)
  }
  element.textContent = JSON.stringify(data)
}
